import { Link, useLocation, useSearchParams } from "react-router";

import { Button } from "~/components/ui/button";
import { cn } from "~/lib/ui";
import { serializeSearchParams } from "~/lib/utils/url";

type PlpPaginationProps = {
  currentPage: number;
  totalPages: number;
  viewAll: boolean;
  pageCap: number;
  total: number;
  shown: number;
  itemsPerPage: number;
  /**
   * COM-431: the most hits a `?page=all` response can carry (server
   * `VIEW_ALL_MAX_RESULTS`). View All fans out in larger CIO pages than Load
   * More, so it can surface MORE than `pageCap * itemsPerPage` — the link must
   * stay offered until the grid holds everything View All could add. Required
   * (no fallback to the Load More ceiling): a broken prop thread would silently
   * restore the 360-hit cliff this fixes.
   */
  viewAllMaxResults: number;
  offsetStart: number;
  onLoadMore: () => void;
  isLoadingMore: boolean;
  className?: string;
};

function buildPageHref(
  pathname: string,
  searchParams: URLSearchParams,
  page: number | "all" | null,
) {
  const next = new URLSearchParams(searchParams);
  if (page === null || page === 1) {
    next.delete("page");
  } else {
    next.set("page", String(page));
  }
  // serializeSearchParams (not next.toString()) so SSR-rendered pagination
  // hrefs match prod byte-for-byte — literal commas in multi-value facets,
  // sorted params (SFCC-11657).
  const qs = serializeSearchParams(next);
  return qs ? `${pathname}?${qs}` : pathname;
}

export function PlpPagination({
  currentPage,
  totalPages,
  viewAll,
  pageCap,
  total,
  shown,
  itemsPerPage,
  viewAllMaxResults,
  offsetStart,
  onLoadMore,
  isLoadingMore,
  className,
}: PlpPaginationProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // The most a Load-More-to-cap session can ever surface. Once `shown`
  // reaches it, Load More has nothing left to fetch.
  const loadMoreReachable = Math.min(total, pageCap * itemsPerPage);
  const nothingMoreToShow = shown >= loadMoreReachable;
  // Pages-worth currently on screen (ceil: dedupe can leave partial windows).
  // Gates Load More when the grid already holds every reachable page even
  // though dedupe kept `shown` below `loadMoreReachable` — the fetch would be
  // a clamped-duplicate no-op (computeNextLoadMorePage returns null there too).
  const pagesShown = Math.ceil(shown / itemsPerPage);
  const hasMore =
    !viewAll &&
    currentPage < totalPages &&
    currentPage < pageCap &&
    pagesShown < pageCap &&
    !nothingMoreToShow;
  // COM-431: View All reaches further than Load More (bigger CIO pages), so
  // it stays offered past the Load More ceiling — until the grid already holds
  // everything a `?page=all` response could return.
  const viewAllReachable = Math.min(total, viewAllMaxResults);
  const showViewAll = !viewAll && totalPages > 1 && shown < viewAllReachable;
  // COM-431: a View All page whose listing is bigger than the fan-out ceiling
  // can't offer Load More or View All (both `!viewAll`-gated), so say so
  // instead of ending the grid silently at `viewAllMaxResults` — that silent
  // truncation (at 360) is what the ticket reported. Keyed on `total`, not
  // `shown`, because page-1-wins dedupe can leave `shown` a few below `total`
  // on a complete listing.
  const viewAllTruncated = viewAll && total > viewAllMaxResults;

  // Render nothing only when the grid is empty. The "Showing X - Y of Z
  // products" summary stays visible even when there's no Load More / View All
  // to offer — i.e. a single page (results <= page size) or the last page of a
  // multi-page set. Previously this returned null in those cases, dropping the
  // count entirely (SFCC-11626).
  if (shown <= 0) {
    return null;
  }

  const displayEnd = offsetStart + shown - 1;

  return (
    <div
      data-grid-footer
      className={cn(
        "col-span-full flex flex-col items-center gap-6",
        className,
      )}
    >
      <div className="px-3 text-center">
        <span className="mr-2 font-grotesque text-base font-medium leading-6 text-blue-04">
          Showing {offsetStart} - {displayEnd}
        </span>
        <span className="font-grotesque text-base font-light leading-6 text-blue-04">
          of {total} products
        </span>
      </div>

      {viewAllTruncated && (
        <p
          data-testid="plp-view-all-truncated"
          className="px-3 text-center font-grotesque text-sm font-light leading-5 text-blue-04"
        >
          View All shows the first {shown} products. Use the filters to narrow
          this list and see the rest.
        </p>
      )}

      {(hasMore || showViewAll) && (
        <div className="flex w-full flex-col items-center gap-3">
          {hasMore && (
            <div className="flex items-center justify-center">
              <Button
                variant="cta"
                size="5"
                aria-label="Load more products"
                onClick={onLoadMore}
                disabled={isLoadingMore}
              >
                <span>{isLoadingMore ? "Loading…" : "Load More"}</span>
              </Button>
            </div>
          )}

          {showViewAll && (
            <div className="text-center">
              <Link
                to={buildPageHref(location.pathname, searchParams, "all")}
                className="border-b border-blue-04 bg-transparent p-0 font-grotesque text-sm font-medium leading-none text-blue-04 no-underline"
                // "none", never "intent": hovering this link would fire the
                // full `?page=all` fan-out server-side (≤5 CIO calls of 200 +
                // an up-to-1,000-hit SCAPI enrich) as a speculative prefetch.
                // View All must only cost on real clicks. Kept explicit (it's
                // RR7's default) so nobody "restores" intent.
                prefetch="none"
                // COM-431: `?page=all` is noindex,follow (computePlpRobots owns
                // that verdict) and appears on every PLP / facet combination.
                // Each crawl of it now costs up to 5×200 CIO + a 1,000-hit
                // enrich (~3x the old 360-hit page), so don't invite crawlers
                // down it — shoppers are unaffected. Deliberately NOT a
                // robots.txt Disallow: a blocked URL can't have its noindex /
                // canonical re-read, so already-crawled `?page=all` URLs would
                // linger as "indexed, though blocked".
                rel="nofollow"
                preventScrollReset
              >
                View All
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
